import React, { useState, useEffect, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import couponAdminService from '../../../services/couponAdminService';
import Modal from '../../../components/Modal';

const STATUS_ALL = 'all';
const STATUS_ACTIVE = 'active';
const STATUS_USED = 'used';
const STATUS_EXPIRED = 'expired';

const CouponsPage = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [maxPage, setMaxPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState(STATUS_ALL);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const data = await couponAdminService.getCoupons(currentPage, pageSize, debouncedSearch);
      setCoupons(data.list || []);
      setTotalItems(data.total || 0);
      setMaxPage(data.maxPage || 1);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('Erreur lors du chargement des coupons.');
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearch]);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  const handleDeleteClick = (coupon) => {
    setCouponToDelete(coupon);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!couponToDelete) return;
    setIsSubmitting(true);
    try {
      await couponAdminService.deleteCoupon(couponToDelete.id);
      toast.success('Coupon supprimé');
      setIsDeleteModalOpen(false);
      setCouponToDelete(null);
      fetchCoupons();
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setIsSubmitting(false);
    }
  };

  const now = new Date();

  const getStatusBadge = (coupon) => {
    if (coupon.isUsed) return { label: 'Utilisé', cls: 'bg-gray-100 text-gray-600' };
    if (new Date(coupon.expirationDate) < now) return { label: 'Expiré', cls: 'bg-red-100 text-red-700' };
    return { label: 'Actif', cls: 'bg-emerald-100 text-emerald-700' };
  };

  const filteredCoupons = coupons.filter(c => {
    if (statusFilter === STATUS_ACTIVE) return !c.isUsed && new Date(c.expirationDate) >= now;
    if (statusFilter === STATUS_USED) return c.isUsed;
    if (statusFilter === STATUS_EXPIRED) return !c.isUsed && new Date(c.expirationDate) < now;
    return true;
  });

  const renderPageNumbers = () => {
    if (maxPage <= 1) return null;

    // Build a compact window: first, last, current ±1, with ellipses.
    const pages = [];
    const delta = 1;
    const range = [];
    for (let i = Math.max(1, currentPage - delta); i <= Math.min(maxPage, currentPage + delta); i++) {
      range.push(i);
    }
    if (range[0] > 1) {
      pages.push(1);
      if (range[0] > 2) pages.push('…');
    }
    range.forEach(p => pages.push(p));
    if (range[range.length - 1] < maxPage) {
      if (range[range.length - 1] < maxPage - 1) pages.push('…');
      pages.push(maxPage);
    }

    return pages.map((p, idx) =>
      p === '…' ? (
        <span
          key={`ellipsis-${idx}`}
          className="relative inline-flex items-center px-2 sm:px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-400 select-none"
        >
          …
        </span>
      ) : (
        <button
          key={p}
          onClick={() => setCurrentPage(p)}
          className={`relative inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 bg-white text-sm font-medium ${
            currentPage === p
              ? 'z-10 bg-[#174540]/30 border-[#174540] text-[#174540]'
              : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          {p}
        </button>
      )
    );
  };

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Coupons Roulette</h1>
          <p className="mt-2 text-sm text-gray-700">
            Liste de tous les coupons générés par la roulette.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800">
            {totalItems} coupon{totalItems !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="sm:col-span-2 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
            placeholder="Rechercher par code, nom client ou téléphone..."
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="block w-full pl-3 pr-10 py-2 border border-gray-300 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm rounded-md"
        >
          <option value={STATUS_ALL}>Tous les statuts</option>
          <option value={STATUS_ACTIVE}>Actif</option>
          <option value={STATUS_USED}>Utilisé</option>
          <option value={STATUS_EXPIRED}>Expiré</option>
        </select>
      </div>

      {/* Table */}
      <div className="mt-8">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#174540]" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-500">{error}</div>
        ) : filteredCoupons.length === 0 ? (
          <div className="text-center py-12 bg-white shadow overflow-hidden rounded-lg">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun coupon trouvé</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? "Essayez d'autres termes." : "Aucun coupon généré pour l'instant."}
            </p>
          </div>
        ) : (
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            {/* Desktop */}
            <div className="hidden md:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remise</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiration</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCoupons.map(coupon => {
                    const badge = getStatusBadge(coupon);
                    return (
                      <tr key={coupon.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-mono text-sm font-bold text-gray-800 bg-gray-100 px-2 py-1 rounded">{coupon.code}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{coupon.clientName}</div>
                          <div className="text-xs text-gray-500">{coupon.clientPhone}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <div className="h-7 w-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-emerald-700">{coupon.pourcentage}%</span>
                            </div>
                            <span className="text-sm text-gray-700">de réduction</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(coupon.expirationDate).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${badge.cls}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleDeleteClick(coupon)}
                            className="text-red-600 hover:text-red-900"
                            title="Supprimer"
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile */}
            <div className="md:hidden">
              <ul className="divide-y divide-gray-200">
                {filteredCoupons.map(coupon => {
                  const badge = getStatusBadge(coupon);
                  return (
                    <li key={coupon.id} className="px-4 py-4">
                      <div className="flex justify-between items-start">
                        <div className="space-y-1">
                          <span className="font-mono text-sm font-bold text-gray-800 bg-gray-100 px-2 py-0.5 rounded">{coupon.code}</span>
                          <p className="text-sm text-gray-800">{coupon.clientName}</p>
                          <p className="text-xs text-gray-500">{coupon.clientPhone}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="font-bold text-emerald-700">{coupon.pourcentage}%</span>
                            <span>· Expire {new Date(coupon.expirationDate).toLocaleDateString('fr-FR')}</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`px-2 py-0.5 text-xs rounded-full font-semibold ${badge.cls}`}>{badge.label}</span>
                          <button
                            onClick={() => handleDeleteClick(coupon)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredCoupons.length > 0 && (
        <div className="bg-white px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-t border-gray-200 sm:px-6 mt-6 rounded-lg shadow">
          <div className="flex items-center gap-3">
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }} className="border border-gray-300 rounded-md text-sm py-1 pl-2 pr-7 focus:ring-emerald-500 focus:border-emerald-500">
              {[5, 10, 25, 50].map(s => <option key={s} value={s}>{s} / page</option>)}
            </select>
            <span className="text-sm text-gray-700">{totalItems} coupon{totalItems !== 1 ? 's' : ''} au total</span>
          </div>
          <nav className="relative z-0 inline-flex flex-wrap justify-center rounded-md shadow-sm -space-x-px self-center sm:self-auto">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            {renderPageNumbers()}
            <button
              onClick={() => setCurrentPage(p => Math.min(maxPage, p + 1))}
              disabled={currentPage === maxPage}
              className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === maxPage ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </nav>
        </div>
      )}

      {/* Delete confirm modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmer la suppression"
        onSubmit={handleConfirmDelete}
        size="md"
        isSubmitting={isSubmitting}
        submitLabel="Supprimer"
      >
        <p className="text-sm text-gray-700">
          Êtes-vous sûr de vouloir supprimer le coupon{' '}
          <span className="font-mono font-bold">{couponToDelete?.code}</span> ? Cette action est irréversible.
        </p>
      </Modal>
    </div>
  );
};

export default CouponsPage;
